import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedProducts, getCategories } from '../services/api';
import SEO from '../components/SEO';
import './HomePage.css';

function HomePage({ config }) {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productCount, setProductCount] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch stats, categories and products independently
        const statsPromise = fetch('/api/stats')
          .then(res => res.json())
          .catch(err => {
            console.error('Error fetching stats:', err);
            return { products: 0 };
          });

        const catsPromise = getCategories().catch(err => {
          console.error('Error fetching categories:', err);
          return [];
        });

        const productsPromise = getFeaturedProducts(12).catch(err => {
          console.error('Error fetching products:', err);
          return [];
        });

        const [stats, products, cats] = await Promise.all([statsPromise, productsPromise, catsPromise]);

        setProductCount(stats.products);
        setFeaturedProducts(products || []);
        setCategories((cats || []).slice(0, 8)); // Show first 8 categories
        setLoading(false);
      } catch (error) {
        console.error('Error fetching homepage data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const seoDescription = `${config.name} - Your trusted partner for industrial supplies, equipment, and services. Connecting buyers with verified suppliers across North America.`;

  return (
    <div className="homepage">
      <SEO
        title={`${config.name} - ${config.tagline}`}
        description={seoDescription}
        type="website"
        siteName={config.name}
      />
      {/* Hero Section */}
      <section className="hero" style={{ backgroundColor: config.primaryColor }}>
        <div className="hero-content">
          <h1>{config.name}</h1>
          <p className="hero-tagline">{config.tagline}</p>
          <p className="hero-description">
            Your trusted partner for industrial supplies, equipment, and services.
            Connecting buyers with verified suppliers across North America.
          </p>
          {productCount !== null && productCount > 0 && (
            <div className="hero-stats-row">
              <div className="stat-item">
                <strong>{productCount.toLocaleString()}</strong>
                <span>Products</span>
              </div>
              <div className="stat-item">
                <strong>10,000+</strong>
                <span>Suppliers</span>
              </div>
              <div className="stat-item">
                <strong>24/7</strong>
                <span>Support</span>
              </div>
            </div>
          )}
          <div className="hero-cta">
            <Link to="/search" className="cta-button primary">Browse Products</Link>
            <Link to="/contact" className="cta-button secondary">Request Quote</Link>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-grid">
            <div className="trust-item">
              <h3>✓ Verified Suppliers</h3>
              <p>All suppliers are thoroughly vetted and certified</p>
            </div>
            <div className="trust-item">
              <h3>✓ Quality Guaranteed</h3>
              <p>Premium products meeting industry standards</p>
            </div>
            <div className="trust-item">
              <h3>✓ Fast Shipping</h3>
              <p>Quick delivery across North America</p>
            </div>
            <div className="trust-item">
              <h3>✓ Expert Support</h3>
              <p>Technical assistance from industry professionals</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2>Shop by Category</h2>
            <p className="section-description">
              Explore our comprehensive range of industrial products and equipment.
              From machinery and tools to automation systems and process equipment.
            </p>
          </div>
          {categories.length > 0 ? (
            <>
              <div className="categories-grid">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="category-card"
                  >
                    <div className="category-image">
                      {category.image ? (
                        <img src={category.image} alt={category.name} />
                      ) : (
                        <div className="category-placeholder">
                          <span>{category.name}</span>
                        </div>
                      )}
                    </div>
                    <h3>{category.name}</h3>
                    {category.description && <p>{category.description}</p>}
                  </Link>
                ))}
              </div>
              <div className="view-all-link">
                <Link to="/search">View All Categories →</Link>
              </div>
            </>
          ) : (
            <p>Loading categories...</p>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us">
        <div className="container">
          <h2>Why Choose {config.name}?</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">🏭</div>
              <h3>Industry Expertise</h3>
              <p>Over 20 years of experience serving industrial, manufacturing, and commercial sectors</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔍</div>
              <h3>Extensive Selection</h3>
              <p>Access to thousands of products from leading manufacturers and distributors</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💼</div>
              <h3>Business Solutions</h3>
              <p>Customized procurement solutions, volume pricing, and dedicated account management</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <h3>Fast Turnaround</h3>
              <p>Quick quotes, expedited shipping, and emergency order capabilities</p>
            </div>
          </div>
        </div>
      </section>

      {featuredProducts.length > 0 && (
        <section className="featured-section">
          <h2>Featured Products</h2>
          <div className="products-grid">
            {featuredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.slug || product.id}`}
                className="product-card"
              >
                <div className="product-image">
                  {product.images && product.images.length > 0 ? (
                    <img src={product.images[0].src} alt={product.name} />
                  ) : (
                    <div className="no-image">No Image</div>
                  )}
                </div>
                <h3>{product.name}</h3>
                <div className="product-price">
                  {product.sale_price ? (
                    <>
                      <span className="sale-price">${product.sale_price}</span>
                      <span className="regular-price">${product.regular_price}</span>
                    </>
                  ) : (
                    <span className="price">${product.regular_price}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default HomePage;
